import React, { useState, useEffect, useRef } from 'react';
import Map from 'ol/Map';
import View from 'ol/View';
import GeoJSON from 'ol/format/GeoJSON';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Overlay from 'ol/Overlay';
import { MouseWheelZoom, defaults as defaultInteractions } from 'ol/interaction';
import { Style, Fill, Stroke } from 'ol/style';

// 📊 Función para generar cortes de rango por cuantiles dinámicos
export const calcularRangosDinamicos = (proyectosPorPartido) => {
    // Extraemos solo las cantidades mayores a 0 y las ordenamos de menor a mayor
    const valores = proyectosPorPartido
        .map(p => parseInt(p.cantidad || 0))
        .filter(v => v > 0)
        .sort((a, b) => a - b);

    if (valores.length === 0) {
        return [0, 0, 0, 0, 0];
    }

    // Dividimos el universo de datos en 5 partes iguales (cuantiles)
    const q1 = valores[Math.floor(valores.length * 0.2)] || 1;
    const q2 = valores[Math.floor(valores.length * 0.4)] || 2;
    const q3 = valores[Math.floor(valores.length * 0.6)] || 5;
    const q4 = valores[Math.floor(valores.length * 0.8)] || 15;
    const max = valores[valores.length - 1] || 100;

    // Retornamos los límites superiores de cada rango
    return [q1, q2, q3, q4, max];
};

// ✂️ Recorta el blanco sobrante alrededor del contenido. OpenLayers dibuja el
// recuadro completo del contenedor, así que un mapa más angosto que su tarjeta
// (la provincia en una tarjeta apaisada) queda flanqueado por blanco: al
// ajustar la imagen a su contenido, el mapa entra mucho más grande en el PDF.
// Devuelve la caja del contenido (o null si todo es blanco).
const recortarBlanco = (canvas, tolerancia = 6) => {
    const { width: W, height: H } = canvas;
    const { data } = canvas.getContext('2d').getImageData(0, 0, W, H);
    let minX = W, minY = H, maxX = -1, maxY = -1;
    for (let y = 0; y < H; y++) {
        const fila = y * W;
        for (let x = 0; x < W; x++) {
            const i = (fila + x) * 4;
            const a = data[i + 3];
            // "con contenido" = opaco y no casi-blanco
            if (a > 10 && (data[i] < 255 - tolerancia || data[i + 1] < 255 - tolerancia || data[i + 2] < 255 - tolerancia)) {
                if (x < minX) minX = x;
                if (x > maxX) maxX = x;
                if (y < minY) minY = y;
                if (y > maxY) maxY = y;
            }
        }
    }
    if (maxX < 0) return null;
    return { x: minX, y: minY, width: maxX - minX + 1, height: maxY - minY + 1 };
};

// ✂️ Recorta el canvas compuesto al contenido (caja ya calculada por
// recortarBlanco) y devuelve un canvas nuevo, o el mismo si no hay nada que
// recortar. Separado de capturarMapa para poder capturar tanto a resolución de
// pantalla como a alta resolución.
const recortarContenido = (out, caja) => {
    if (!caja) return out;
    const margen = Math.max(4, Math.round(Math.min(caja.width, caja.height) * 0.02));
    const x = Math.max(0, caja.x - margen);
    const y = Math.max(0, caja.y - margen);
    const w = Math.min(out.width - x, caja.width + margen * 2);
    const h = Math.min(out.height - y, caja.height + margen * 2);
    if (w <= 0 || h <= 0 || (w >= out.width && h >= out.height)) return out;
    const cropped = document.createElement('canvas');
    cropped.width = w;
    cropped.height = h;
    const cctx = cropped.getContext('2d');
    cctx.fillStyle = '#ffffff';
    cctx.fillRect(0, 0, w, h);
    cctx.drawImage(out, x, y, w, h, 0, 0, w, h);
    return cropped;
};

// 🖼️ Captura el canvas del mapa sobre fondo blanco y lo recorta al contenido.
// Si sale todo blanco (el mapa no llegó a pintar polígonos) lo avisa: es el
// síntoma de que el PDF recibiría un recuadro vacío con la leyenda al lado.
const componerCaptura = (canvas) => {
    const out = document.createElement('canvas');
    out.width = canvas.width;
    out.height = canvas.height;
    const ctx = out.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, out.width, out.height);
    ctx.drawImage(canvas, 0, 0);

    const caja = recortarBlanco(out);
    if (!caja) {
        console.warn('[Mapas] La captura salió toda blanca: el mapa todavía no había pintado polígonos. Revisar que la fuente GeoJSON esté cargada y el contenedor tenga tamaño.');
        return out;
    }
    return recortarContenido(out, caja);
};

// 🖼️ Captura del mapa como PNG (fondo blanco). Solo se convierte a raster el
// MAPA individual (como los PNG de los gráficos), no la página entera: el PDF
// lo recibe como una <Image> atómica dentro del layout de texto de react-pdf,
// así el salto de página lo maneja el renderer sin cortar nada por la mitad.
// Devuelve { src, ratio } y el tablero lo usa para escalar cada mapa con su
// proporción real (ya recortada al contenido, sin el blanco de la tarjeta).
//
// NOTA IMPORTANTE: primero renderiza a la resolución de pantalla, y si esa
// captura sale vacía (todo blanco: el mapa todavía no pintó polígonos, p.ej. se
// generó el PDF apenas cargó o con el contenedor ya redimensionado) fuerza un
// updateSize() + renderSync() y vuelve a intentar. Eso es lo que hacía que el
// mapa de la Provincia llegara en blanco con solo la leyenda al lado.
//
// Además captura en ALTA resolución: en el PDF el mapa se imprime bastante más
// grande que su tamaño en px, así que a 1x se ve pixelado. Se sube el pixel
// ratio temporalmente antes de capturar y se restaura después, sin tocar la
// apariencia en pantalla.
const capturarMapa = (instancia) => {
    if (!instancia) return null;
    const viewport = instancia.getViewport();
    if (!viewport.querySelector('canvas')) return null;

    const ratioPantalla = instancia.getPixelRatio() || 1;
    const ratioAlta = Math.min(4, Math.max(3, ratioPantalla * 2));

    let final = null;
    try {
        instancia.setPixelRatio(ratioAlta);
        instancia.updateSize();
        try {
            instancia.renderSync();
        } catch (e) {
            // si está en pleno ciclo de render, el canvas ya está actualizado
        }
        const canvas = viewport.querySelector('canvas');
        if (canvas && canvas.width > 0 && canvas.height > 0) {
            final = componerCaptura(canvas);
        }
    } finally {
        // Restauramos la resolución normal para que la pantalla no quede alterada
        instancia.setPixelRatio(ratioPantalla);
        instancia.updateSize();
        try {
            instancia.renderSync();
        } catch (e) {
            // idem: ya se re-renderizó
        }
    }

    if (!final || !final.width || !final.height) return null;
    return { src: final.toDataURL('image/png'), ratio: final.width / final.height };
};


export const ComponenteMapas = ({ proyectosPorPartido = [], partidoSeleccionadoId = null, onMapasListos = null }) => {
    const mapaProvinciaRef = useRef(null);
    const mapaConurbanoRef = useRef(null);

    // 🎯 onMapasListos se guarda en un ref para que el useEffect de creación de
    // los mapas (dep []: corre UNA sola vez) use siempre el callback actual.
    const onMapasListosRef = useRef(onMapasListos);
    onMapasListosRef.current = onMapasListos;

	// 🎯 ESTADO CLAVE: Controla si la rueda del mouse afecta al mapa o a la página
    const [permitirZoom, setPermitirZoom] = useState(false);

	// 1. Declará estas dos referencias arriba del useEffect principal
	// 🎯 Nacen como instancias limpias de zoom por rueda
	const mouseWheelProvincia = useRef(new MouseWheelZoom());
	const mouseWheelConurbano = useRef(new MouseWheelZoom());

	const mapaProvinciaInstancia = useRef(null);
	const mapaConurbanoInstancia = useRef(null);

	// 🎯 1. Calculamos los rangos dinámicos basados en la data actual
    const rangos = React.useMemo(() => {
        return calcularRangosDinamicos(proyectosPorPartido);
    }, [proyectosPorPartido]);

	// 1. Armamos el mapa indexado por ID numérico (partido_id)
	const estructuraDatos = React.useMemo(() => {
		const mapa = new Map();
		proyectosPorPartido.forEach(p => {
			if (p.partido_id) {
				// Forzamos que sea un entero para evitar problemas de tipo (String vs Number)
				mapa.set(parseInt(p.partido_id), parseInt(p.cantidad || 0));
			}
		});
		return mapa;
	}, [proyectosPorPartido]);

	// 📌 REFS: los mapas se crean UNA sola vez; estos refs se actualizan con los datos vivos
	// para que los estilos y el tooltip lean SIEMPRE los valores actuales sin destruir el mapa.
	const estructuraDatosRef = useRef(estructuraDatos);
	const partidoSeleccionadoIdRef = useRef(partidoSeleccionadoId);
	const rangosRef = useRef(rangos);
	const capaProvinciaRef = useRef(null);
	const capaConurbanoRef = useRef(null);

	// 🎨 2. Nueva paleta de azules escalonada (lee de los refs para no capturar datos viejos)
    const obtenerEstiloPoligono = (feature) => {
        const idPartidoGeo = parseInt(feature.get('partido_nro'));
        const cantidad = estructuraDatosRef.current.get(idPartidoGeo) || 0;

        // 1. 🌍 CAPA BASE: Color por cuantil (Mantiene la foto general de la PBA)
        let colorRelleno = '#e2e8f0'; 
        if (cantidad > 0) {
            if (cantidad <= rangosRef.current[0]) colorRelleno = '#bae6fd';
            else if (cantidad <= rangosRef.current[1]) colorRelleno = '#7dd3fc';
            else if (cantidad <= rangosRef.current[2]) colorRelleno = '#38bdf8';
            else if (cantidad <= rangosRef.current[3]) colorRelleno = '#0284c7';
            else colorRelleno = '#0369a1';
        }

        // 2. 🎯 CAPA DE DESTACADO: Evaluamos si este polígono es el seleccionado en el combo
        // Normalizamos el filtro ya sea que venga como String o Array ['Id']
        const partidoSeleccionadoIdActual = partidoSeleccionadoIdRef.current;
        const filtroArray = Array.isArray(partidoSeleccionadoIdActual) 
            ? partidoSeleccionadoIdActual.map(p => p.toString()) 
            : [partidoSeleccionadoIdActual?.toString()];

        const esElSeleccionado = partidoSeleccionadoIdActual && 
                                 !filtroArray.includes('Todos') && 
                                 filtroArray.includes(idPartidoGeo.toString());

        if (esElSeleccionado) {
            return new Style({
                fill: new Fill({ color: colorRelleno }), // Mantiene su color de rango original de fondo
                stroke: new Stroke({
                    color: '#ef4444', // 🔥 Anillo Rojo brillante para el municipio seleccionado
                    width: 3.5,
                }),
                zIndex: 999 // Lo dibuja por encima del resto
            });
        }

        // Estilo estándar para los demás partidos
        return new Style({
            fill: new Fill({ color: colorRelleno }),
            stroke: new Stroke({ color: '#ffffff', width: 0.8 })
        });
    };

	// 📌 Mantenemos los refs sincronizados con los datos vivos (para estilos y tooltip)
	React.useEffect(() => { estructuraDatosRef.current = estructuraDatos; }, [estructuraDatos]);
	React.useEffect(() => { partidoSeleccionadoIdRef.current = partidoSeleccionadoId; }, [partidoSeleccionadoId]);
	React.useEffect(() => { rangosRef.current = rangos; }, [rangos]);

	// 🎨 Cuando cambian los datos, forzamos el redibujo de las capas (sin recrear el mapa ni el tooltip)
	React.useEffect(() => {
		capaProvinciaRef.current?.changed();
		capaConurbanoRef.current?.changed();
	}, [estructuraDatos, partidoSeleccionadoId, rangos]);

	useEffect(() => {
		if (!mapaProvinciaRef.current || !mapaConurbanoRef.current) return;

		// 📌 Buscamos los elementos HTML individuales
		const elProvincia = document.getElementById('tooltip-provincia');
		const elConurbano = document.getElementById('tooltip-conurbano');

		// 🎯 Creamos un Overlay independiente para cada mapa (apuntando al mismo div)
		const overlayProvincia = new Overlay({
			element: elProvincia,
			offset: [10, 10],
			positioning: 'top-left'
		});

		const overlayConurbano = new Overlay({
			element: elConurbano,
			offset: [10, 10],
			positioning: 'top-left'
		});

		// 🗺️ 1. CONFIGURACIÓN MAPA PROVINCIA
		const fuenteProvincia = new VectorSource({
			url: 'https://raw.githubusercontent.com/javiarch/geoms/master/deptos_ARBA.geojson',
			format: new GeoJSON()
		});

		const capaProvincia = new VectorLayer({ source: fuenteProvincia, style: obtenerEstiloPoligono });
		capaProvinciaRef.current = capaProvincia;

		const mapaProvincia = new Map({
			target: mapaProvinciaRef.current,
			layers: [capaProvincia],			
			controls: [],
			interactions: defaultInteractions({ mouseWheelZoom: false }),
			view: new View({ center: [-6700000, -4450000], zoom: 6, minZoom: 5, maxZoom: 9 })
		});
		mapaProvincia.addOverlay(overlayProvincia); // 👈 Agregamos su overlay
		mapaProvinciaInstancia.current = mapaProvincia; // 👈 Guardamos la instancia

		// 🗺️ 2. CONFIGURACIÓN MAPA CONURBANO (RMBA)
		const fuenteConurbano = new VectorSource({
			url: 'https://raw.githubusercontent.com/javiarch/geoms/master/partidos_RMBA_N.geojson',
			format: new GeoJSON()
		});

		const capaConurbano = new VectorLayer({ source: fuenteConurbano, style: obtenerEstiloPoligono });
		capaConurbanoRef.current = capaConurbano;

		const mapaConurbano = new Map({
			target: mapaConurbanoRef.current,
			layers: [capaConurbano],			
			controls: [],
			interactions: defaultInteractions({ mouseWheelZoom: false }),
			view: new View({ center: [-6500000, -4100000], zoom: 9, minZoom: 8, maxZoom: 12 })
		});
		mapaConurbano.addOverlay(overlayConurbano); // 👈 Agregamos su overlay
		mapaConurbanoInstancia.current = mapaConurbano; // 👈 Guardamos la instancia

		// 🖨️ Exponemos la captura de ambos mapas para el PDF del tablero
		onMapasListosRef.current?.({
			capturarProvincia: () => capturarMapa(mapaProvincia),
			capturarConurbano: () => capturarMapa(mapaConurbano),
		});

		// Ajustar encuadres automáticos al cargar
		fuenteProvincia.once('change', () => {
			if (fuenteProvincia.getState() === 'ready') {
				mapaProvincia.getView().fit(fuenteProvincia.getExtent(), { padding: [20, 20, 20, 20] });
			}
		});
		fuenteConurbano.once('change', () => {
			if (fuenteConurbano.getState() === 'ready') {
				mapaConurbano.getView().fit(fuenteConurbano.getExtent(), { padding: [10, 10, 10, 10] });
			}
		});

		// -------------------------------------------------------------------------
		// 🎛️ LÓGICA DEL HOVER/POINTERMOVE COMPARTIDA
		// -------------------------------------------------------------------------
		const manejarMoverPuntero = (evt, mapaActivo, overlayActivo, elementoHtml) => {
			// 🛡️ CONTROL CRÍTICO: Si el elemento HTML del tooltip no existe en el DOM en este instante,
			// salimos de la función inmediatamente para evitar el Uncaught TypeError.
			if (!elementoHtml) return;

			const feature = mapaActivo.forEachFeatureAtPixel(evt.pixel, (feat) => feat);

			if (feature) {
				const idPartidoGeo = parseInt(feature.get('partido_nro'));
				const nombrePartido = feature.get('nam') || 'Desconocido';
				const cantidad = estructuraDatosRef.current.get(idPartidoGeo) || 0;

				elementoHtml.innerHTML = `
            <div class="font-bold border-b border-gray-700 pb-1 mb-1 text-gray-200">${nombrePartido}</div>
            <div class="text-emerald-400 font-semibold text-right">${cantidad} ${cantidad === 1 ? 'Proyecto' : 'Proyectos'}</div>
        `;

				elementoHtml.style.opacity = '1';
				overlayActivo.setPosition(evt.coordinate);
				mapaActivo.getTargetElement().style.cursor = 'pointer';
			} else {
				elementoHtml.style.opacity = '0';
				overlayActivo.setPosition(undefined);
				mapaActivo.getTargetElement().style.cursor = 'grab';
			}
		};

		// Vinculamos pasándole el combo completo e independiente a cada uno
		mapaProvincia.on('pointermove', (e) => manejarMoverPuntero(e, mapaProvincia, overlayProvincia, elProvincia));
		mapaConurbano.on('pointermove', (e) => manejarMoverPuntero(e, mapaConurbano, overlayConurbano, elConurbano));

		// Limpieza estricta al desmontar
		return () => {
			mapaProvincia.setTarget(null);
			mapaConurbano.setTarget(null);
			capaProvinciaRef.current = null;
			capaConurbanoRef.current = null;
		};
	}, []);

	// 3. 🎯 EFECTO ADICIONAL: Escucha el cambio del checkbox y activa/desactiva la condición
	// 🎯 CONTROL DINÁMICO DE INTERACCIÓN
	useEffect(() => {
		const mapaProv = mapaProvinciaInstancia.current;
		const mapaConur = mapaConurbanoInstancia.current;

		if (!mapaProv || !mapaConur) return;

		if (permitirZoom) {
			// 🔌 Si el check está activo, inyectamos las interacciones de la rueda
			mapaProv.addInteraction(mouseWheelProvincia.current);
			mapaConur.addInteraction(mouseWheelConurbano.current);
		} else {
			// 🔌 Si se destilda, las removemos por completo del mapa
			mapaProv.removeInteraction(mouseWheelProvincia.current);
			mapaConur.removeInteraction(mouseWheelConurbano.current);
		}
	}, [permitirZoom]);

    return (
        <div className="w-full flex flex-col mb-6">

			<div className="w-full flex flex-col xl:flex-row gap-6">
				{/* MAPA GENERAL PROVINCIA */}
				<div className="xl:flex-[2] bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex flex-col h-[550px]">                
					
					<div className="mb-3 flex justify-between items-center">
						<h3 className="text-base font-bold text-gray-800 tracking-tight uppercase">Distribución Provincial de Obras</h3>
						
						<div className="flex items-center gap-3">
							{/* 🎛️ CHECKBOX HABILITAR ZOOM */}
							<label className="flex items-center space-x-2 text-xs text-gray-600 cursor-pointer bg-white hover:bg-gray-50 px-3 py-2 rounded-xl border border-gray-200 shadow-sm transition-colors select-none">
								<input
									type="checkbox"
									className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer w-4 h-4"
									checked={permitirZoom}
									onChange={(e) => setPermitirZoom(e.target.checked)}
								/>
								<span className="font-medium text-gray-700">Habilitar zoom</span>
							</label>

							{/* Leyenda simple incrustada */}
							<div className="flex flex-wrap items-center gap-3 text-xs text-gray-600 bg-gray-50/80 p-2.5 rounded-xl border border-gray-100 backdrop-blur-sm shadow-inner">
							{/* Casillero 0 */}
							<div className="flex items-center space-x-1.5">
								<span className="w-3.5 h-3.5 bg-[#e2e8f0] inline-block rounded border border-gray-300 shadow-sm"></span>
								<span className="font-medium">0</span>
							</div>

							{/* Rango 1 */}
							<div className="flex items-center space-x-1.5">
								<span className="w-3.5 h-3.5 bg-[#bae6fd] inline-block rounded shadow-sm"></span>
								<span className="font-medium">1 - {rangos[0]}</span>
							</div>

							{/* Rango 2 */}
							<div className="flex items-center space-x-1.5">
								<span className="w-3.5 h-3.5 bg-[#7dd3fc] inline-block rounded shadow-sm"></span>
								<span className="font-medium">{rangos[0] + 1} - {rangos[1]}</span>
							</div>

							{/* Rango 3 */}
							<div className="flex items-center space-x-1.5">
								<span className="w-3.5 h-3.5 bg-[#38bdf8] inline-block rounded shadow-sm"></span>
								<span className="font-medium">{rangos[1] + 1} - {rangos[2]}</span>
							</div>

							{/* Rango 4 */}
							<div className="flex items-center space-x-1.5">
								<span className="w-3.5 h-3.5 bg-[#0284c7] inline-block rounded shadow-sm"></span>
								<span className="font-medium">{rangos[2] + 1} - {rangos[3]}</span>
							</div>

							{/* Rango 5 (Máximos) */}
							<div className="flex items-center space-x-1.5">
								<span className="w-3.5 h-3.5 bg-[#0369a1] inline-block rounded shadow-sm"></span>
								<span className="font-medium">{rangos[3] + 1} +</span>
							</div>
						</div>
					</div>
					</div>
					<div ref={mapaProvinciaRef} className="flex-1 w-full rounded-lg bg-slate-50 overflow-hidden cursor-grab active:cursor-grabbing" />
					{/* 🎯 TOOLTIPS FLOTANTES EXCLUSIVOS */}
					<div id="tooltip-provincia" className="absolute z-50 bg-gray-900/95 text-white text-xs px-3 py-2 rounded-lg shadow-md pointer-events-none transition-opacity duration-150 opacity-0 font-sans border border-gray-700 min-w-[120px]" />
				</div>

				{/* MAPA REGIÓN METROPOLITANA (CONURBANO) */}
				<div className="xl:flex-[1] bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex flex-col h-[550px]">
					<div className="mb-3">
						<h3 className="text-base font-bold text-gray-800 tracking-tight uppercase">Región Metropolitana (RMBA)</h3>
					</div>
					<div ref={mapaConurbanoRef} className="flex-1 w-full rounded-lg bg-slate-50 overflow-hidden cursor-grab active:cursor-grabbing" />
					<div id="tooltip-conurbano" className="absolute z-50 bg-gray-900/95 text-white text-xs px-3 py-2 rounded-lg shadow-md pointer-events-none transition-opacity duration-150 opacity-0 font-sans border border-gray-700 min-w-[120px]" />

				</div>
			</div>

        </div>
    );
};